<template>
  <div>
    <b-card class="rounded-lg">
      <b-card-text class="d-flex">
        {{ $t("ErrorLogCard.title") }}
      </b-card-text>

      <b-card-text>
        <b-card no-body>
          <div class="m-2">
            <b-row>
              <b-col
                cols="12"
                md="6"
                class="d-flex align-items-center justify-content-start mb-1 px-0 mb-md-0"
              >
                <b-form-input
                  v-model="searchQuery"
                  :placeholder="$t('ErrorLogCard.searchPlaceHolder')"
                />
              </b-col>

              <b-col
                cols="12"
                md="6"
                class="d-flex align-items-center justify-content-end mb-1 px-0 mb-md-0"
              >
                <b-button
                  variant="primary"
                  @click="triggerSync"
                  :disabled="syncLoading"
                >
                  <span v-if="syncLoading">
                    <b-spinner small type="grow" class="mr-50" />
                    {{ $t("Message.loading") }}...
                  </span>
                  <span v-else>
                    <feather-icon icon="RefreshCwIcon" class="mr-50" />
                    {{ $t("ErrorLogCard.triggerSyncBtn") }}
                  </span>
                </b-button>
              </b-col>

              <b-table
                ref="table"
                responsive
                :items="items"
                :fields="tableColumns"
                primary-key="id"
                show-empty
                :sort-desc.sync="sortDesc"
                :sort-by.sync="sortBy"
                :empty-text="$t('ErrorLogCard.noMatchRecordFound')"
                class="position-relative mt-2"
              >
                <template #head(checkbox)>
                  <div class="d-flex" style="margin-top: 1px">
                    <b-form-checkbox
                      v-model="selected"
                      value="A"
                      class="custom-control-primary my-auto"
                    />
                  </div>
                </template>
                <template #head(date)="data">
                  <div class="d-flex">
                    <span class="my-auto" style="margin-right: 5px">
                      {{ $t(`ErrorLogCard.${data.field.label}`) }}
                    </span>

                    <span class="d-flex flex-column position-relative">
                      <feather-icon
                        size="10"
                        class="cursor-pointer"
                        icon="ChevronUpIcon"
                        style="cursor: pointer"
                        role="button"
                        @click="sortingChanged(data, true)"
                      />

                      <feather-icon
                        size="10"
                        class="cursor-pointer"
                        icon="ChevronDownIcon"
                        role="button"
                        @click="sortingChanged(data, false)"
                      />
                    </span>
                  </div>
                </template>
                <template #head(invoice)="data">
                  <div class="d-flex">
                    <span class="my-auto" style="margin-right: 5px">
                      {{ $t(`ErrorLogCard.${data.field.label}`) }}
                    </span>

                    <span class="d-flex flex-column position-relative">
                      <feather-icon
                        size="10"
                        class="cursor-pointer"
                        icon="ChevronUpIcon"
                        style="cursor: pointer"
                        role="button"
                        @click="sortingChanged(data, true)"
                      />

                      <feather-icon
                        size="10"
                        class="cursor-pointer"
                        icon="ChevronDownIcon"
                        role="button"
                        @click="sortingChanged(data, false)"
                      />
                    </span>
                  </div>
                </template>
                <template #head(activity)="data">
                  <div class="d-flex">
                    <span class="my-auto" style="margin-right: 5px">
                      {{ $t(`ErrorLogCard.${data.field.label}`) }}
                    </span>

                    <span class="d-flex flex-column position-relative">
                      <feather-icon
                        size="10"
                        class="cursor-pointer"
                        icon="ChevronUpIcon"
                        style="cursor: pointer"
                        role="button"
                        @click="sortingChanged(data, true)"
                      />

                      <feather-icon
                        size="10"
                        class="cursor-pointer"
                        icon="ChevronDownIcon"
                        role="button"
                        @click="sortingChanged(data, false)"
                      />
                    </span>
                  </div>
                </template>
                <template #head(message)="data">
                  <div class="d-flex">
                    <span class="my-auto" style="margin-right: 5px">
                      {{ $t(`ErrorLogCard.${data.field.label}`) }}
                    </span>

                    <span class="d-flex flex-column position-relative">
                      <feather-icon
                        size="10"
                        class="cursor-pointer"
                        icon="ChevronUpIcon"
                        style="cursor: pointer"
                        role="button"
                        @click="sortingChanged(data, true)"
                      />

                      <feather-icon
                        size="10"
                        class="cursor-pointer"
                        icon="ChevronDownIcon"
                        role="button"
                        @click="sortingChanged(data, false)"
                      />
                    </span>
                  </div>
                </template>

                <!-- Column: date -->
                <template #cell(checkbox)="data">
                  <b-form-checkbox v-model="data.value" />
                </template>

                <!-- Column: date -->
                <template #cell(date)="data">
                  <div class="d-flex">
                    <p class="font-weight-bold mb-0">
                      {{ data.value }}
                    </p>
                  </div>
                </template>

                <!-- Column: Status -->
                <template #cell(status)="data">
                  <feather-icon
                    :icon="
                      data.item.status === 'success'
                        ? 'CheckCircleIcon'
                        : 'XCircleIcon'
                    "
                    :class="
                      data.item.status === 'success'
                        ? 'text-success'
                        : 'text-danger'
                    "
                  />
                </template>

                <!-- Column: Message -->
                <template #cell(message)="data">
                  <span class="text-nowrap checking">
                    {{ data.value }}
                  </span>
                </template>
              </b-table>
            </b-row>
          </div>

          <!-- Table -->

          <div class="mx-2 mb-2">
            <b-row align-h="end">
              <!-- Pagination -->
              <b-col
                cols="12"
                sm="6"
                class="d-flex align-items-center justify-content-center justify-content-sm-end"
              >
                <b-pagination
                  v-model="pagination.currentPage"
                  :total-rows="pagination.totalInvoices"
                  :per-page="pagination.perPage"
                  first-number
                  last-number
                  class="mb-0 mt-1 mt-sm-0"
                  prev-class="prev-item"
                  next-class="next-item"
                >
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-card>
      </b-card-text>
    </b-card>
  </div>
</template>

<script>
import { dateMixins } from "@/mixins/dateMixins";
import vSelect from "vue-select";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import useAppConfig from "@core/app-config/useAppConfig";

export default {
  components: {
    vSelect,
    ToastificationContent,
  },
  mixins: [dateMixins],
  data() {
    return {
      config: useAppConfig(),
      selected: false,
      searchQuery: "",
      sortDesc: null,
      syncLoading: false,
      items: [],
      tableColumns: [
        {
          key: "checkbox",
          tdClass: "bTableThStyle",
          thStyle: { width: "20px", padding: "0.72rem 10px" },
        },
        {
          key: "date",
          label: "dateHead",
          tdClass: "bTableTdDateStyle",
          thStyle: { padding: "0.72rem 0" },
        },
        {
          key: "invoice",
          label: "invoiceHead",
        },
        {
          key: "activity",
          label: "activityHead",
        },
        {
          key: "status",
          label: "statusHead",
          thStyle: { padding: "0.72rem 0", width: "40px" },
        },
        {
          key: "message",
          label: "messageHead",
        },
      ],
      pagination: {
        currentPage: 1,
        totalInvoices: 0,
        perPage: 20,
      },
      sortBy: "",
    };
  },

  computed: {
    messageLang() {
      if (this.getLang == "") {
        return this.swedishLang ? "swe_message" : "eng_message";
      }
      return this.getLang == "sv" ? "swe_message" : "eng_message";
    },
    getLang() {
      return this.config.lang.value;
    },
    swedishLang() {
      return localStorage.getItem("mainLang") == "sv";
    },
  },

  watch: {
    searchQuery(value) {
      this.$useJwt.eventsList({ params: { search: value } }).then((res) => {
        const { results } = res.data;

        const { count } = res.data;

        this.pagination.totalInvoices = count;

        this.renderEventList(results);
      });
    },
    "pagination.currentPage": {
      handler(page) {
        this.$useJwt.eventsList({ params: { page } }).then((res) => {
          const { results } = res.data;

          const { count } = res.data;

          this.pagination.totalInvoices = count;

          this.renderEventList(results);
        });
      },
    },
  },

  mounted() {
    this.getAllEvents();
  },

  methods: {
    renderEventList(value) {
      const dataArray = [];

      value.forEach((element) => {
        dataArray.push({
          uuid: element.uuid,
          checkbox: false,
          date: this.getDateStringFormat(element.created_at),
          invoice: element.invoice_number,
          message: element.message,
          activity: element.action,
          status: element.status,
        });
      });

      this.items = dataArray;
    },
    getAllEvents() {
      this.$useJwt.eventsList().then((res) => {
        const { results } = res.data;

        const { count } = res.data;

        this.pagination.totalInvoices = count;

        this.renderEventList(results);
      });
    },

    async triggerSync() {
      if (this.syncLoading) return;
      this.syncLoading = true;
      try {
        const response = await this.$useJwt.triggerSync();
        // Display success message
        if (response.data && response.data[this.messageLang]) {
          this.popup(
            this.$t("Message.Success"),
            "CheckIcon",
            "success",
            response.data[this.messageLang]
          );
        }
        this.getAllEvents();
      } catch (e) {
        // Display error message
        if (
          e.response &&
          e.response.data &&
          e.response.data[this.messageLang]
        ) {
          this.popup(
            this.$t("Message.Error"),
            "AlertTriangleIcon",
            "danger",
            e.response.data[this.messageLang]
          );
        }
      } finally {
        this.syncLoading = false;
      }
    },

    popup(title, icon, variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title,
          icon,
          variant,
          text,
        },
      });
    },

    sortingChanged(sortBy, desc) {
      this.sortBy = sortBy.column;

      this.sortDesc = desc;
    },
  },
};
</script>

<style scoped lang="scss">
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
